/**
 * Activity search filters (TRAV23 activities-front merge).
 *
 * The activities front reused the hotel `filters` slice under the same `filtersStore`
 * key. In the merged app both verticals are mounted at once, so they must not share
 * state: the facet vocabularies differ (activities filter by time of day, duration,
 * guide language and a category tree; hotels by star rating, amenities and
 * certificate), and a stale hotel amenity would silently narrow an activity search.
 *
 * This slice therefore owns the activity half under `activityFiltersStore`, and
 * `@share/store/slices/filters` is left untouched for hotels.
 *
 * Note `ratings` vs the hotel slice's `starRatings`: activities carry a 0-5 review
 * score, not a star classification, so the different name is deliberate.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IHotelAutocompleteItem, IHotelAutocompleteResponse } from '@share/common-types';
import {
  HomesTypes,
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  SESSION_SEARCH_EXPIRED_MESSAGE,
  SortTypes,
  Urls,
} from '@share/constants';
import { AppThunk, axiosInstance, getHeaders } from '@share/utils';

const ZERO_ITEM = 0;
const OK_STATUS = 200;

export interface IActivityFiltersState {
  propertyName: string;
  distance: number;
  /** Activity review score (0-5), not a hotel star classification. */
  ratings: number[];
  amenities: string[];
  accommodationType: string[];
  accessibility: string[];
  popularLocations: string[];
  refundable: string[];
  budget: string[];
  neighbourhoods: string[];
  places: string[];
  flexibleDates: string[];
  sortBy: SortTypes;
  autocompleteItems: IHotelAutocompleteItem[];
  autocompleteLoading: boolean;
  selectedAutocompleteItem: IHotelAutocompleteItem | null;
  selectedAutocompleteLabel: string;
  boardNames: string[];
  searchHomes: HomesTypes;
  hideRatings: boolean | number[];

  /** Facet keys echoed back by the activity search counters. */
  timeOfDays: string[];
  specials: string[];
  guideLanguages: string[];
  durations: string[];
  /** Single selected category key; the UI renders the nested tree around it. */
  activityCategory: string;
}

export const activityFiltersInitialState: IActivityFiltersState = {
  sortBy: SortTypes.BiggestSavingsPercentage,
  propertyName: '',
  distance: 10,
  budget: [],
  neighbourhoods: [],
  places: [],
  ratings: [],
  amenities: [],
  popularLocations: [],
  accommodationType: [],
  accessibility: [],
  refundable: [],
  autocompleteItems: [],
  flexibleDates: [],
  autocompleteLoading: false,
  selectedAutocompleteItem: null,
  selectedAutocompleteLabel: '',
  boardNames: [],
  searchHomes: HomesTypes.All,
  hideRatings: false,

  timeOfDays: [],
  specials: [],
  guideLanguages: [],
  durations: [],
  activityCategory: '',
};

/** Capital Vacations accounts order activities by rating rather than by savings. */
export const activityFiltersCapitalVacationsInitialState: IActivityFiltersState = {
  ...activityFiltersInitialState,
  sortBy: SortTypes.RatingDesc,
};

const activityFiltersSlice = createSlice({
  name: 'activityFilters',
  initialState: activityFiltersInitialState,
  reducers: {
    setSortBy: (state, { payload }: PayloadAction<SortTypes>) => {
      state.sortBy = payload;
    },
    setPropertyName: (state, { payload }: PayloadAction<string>) => {
      state.propertyName = payload;
    },
    setBudget: (state, { payload }: PayloadAction<string[]>) => {
      state.budget = payload;
    },
    setNeighbourhoods: (state, { payload }: PayloadAction<string[]>) => {
      state.neighbourhoods = payload;
    },
    setPopularLocations: (state, { payload }: PayloadAction<string[]>) => {
      state.popularLocations = payload;
    },
    setPlaces: (state, { payload }: PayloadAction<string[]>) => {
      state.places = payload;
    },
    setRating: (state, { payload }: PayloadAction<number[]>) => {
      state.ratings = payload;
    },
    setAmenities: (state, { payload }: PayloadAction<string[]>) => {
      state.amenities = payload;
    },
    setAccommodationType: (state, { payload }: PayloadAction<string[]>) => {
      state.accommodationType = payload;
    },
    setAccessibility: (state, { payload }: PayloadAction<string[]>) => {
      state.accessibility = payload;
    },
    setBoardName: (state, { payload }: PayloadAction<string[]>) => {
      state.boardNames = payload;
    },
    setRefundable: (state, { payload }: PayloadAction<string[]>) => {
      state.refundable = payload;
    },
    setFlexibleDate: (state, { payload }: PayloadAction<string[]>) => {
      state.flexibleDates = payload;
    },
    setDistance: (state, { payload }: PayloadAction<number>) => {
      state.distance = payload;
    },
    /**
     * `isCapitalVacations` picks that account family's default ordering. The activities
     * front carried the full set of hotel profile presets here, but activity search only
     * ever differed on this one, so the rest are not reproduced.
     */
    resetFilters: (_state, { payload }: PayloadAction<boolean | undefined>) => {
      return payload ? activityFiltersCapitalVacationsInitialState : activityFiltersInitialState;
    },
    setFilters: (_state, { payload }: PayloadAction<IActivityFiltersState>) => {
      return payload;
    },
    setAutocompleteItems: (state, { payload }: PayloadAction<IHotelAutocompleteItem[]>) => {
      state.autocompleteItems = payload;
    },
    setAutocompleteLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.autocompleteLoading = payload;
    },
    selectAutocompleteItem: (state, { payload }: PayloadAction<IHotelAutocompleteItem | null>) => {
      state.selectedAutocompleteItem = payload;
    },
    selectAutocompleteLabel: (state, { payload }: PayloadAction<string>) => {
      state.selectedAutocompleteLabel = payload;
    },
    setSearchHomes: (state, { payload }: PayloadAction<HomesTypes>) => {
      state.searchHomes = payload;
    },
    setTimeOfDays: (state, { payload }: PayloadAction<string[]>) => {
      state.timeOfDays = payload;
    },
    setSpecials: (state, { payload }: PayloadAction<string[]>) => {
      state.specials = payload;
    },
    setGuideLanguages: (state, { payload }: PayloadAction<string[]>) => {
      state.guideLanguages = payload;
    },
    setDurations: (state, { payload }: PayloadAction<string[]>) => {
      state.durations = payload;
    },
    setActivityCategory: (state, { payload }: PayloadAction<string>) => {
      state.activityCategory = payload;
    },
  },
});

export const activityFiltersActions = activityFiltersSlice.actions;

export const activityFiltersReducer = activityFiltersSlice.reducer;

/**
 * Activity name autocomplete.
 *
 * Ported from the activities front's `getHotelsAutocomplete`, renamed because it hits
 * the activity endpoint and writes the activity slice. The response carries either
 * `activities` (current contract) or `hotelDetails` (older shape the endpoint still
 * returns for some accounts), so both are mapped onto `IHotelAutocompleteItem`.
 */
export const getActivitiesAutocomplete = (search: string): AppThunk => {
  return async (dispatch, getState) => {
    try {
      dispatch(activityFiltersActions.setAutocompleteLoading(true));

      const { activityStore } = getState();

      const res: { data: IHotelAutocompleteResponse; status: number } = await axiosInstance.post(
        Urls.ActivityAutocomplete,
        {
          sessionKey: activityStore.sessionKey,
          SearchText: search,
        },
        {
          ...getHeaders(),
        },
      );

      const items =
        res?.status === OK_STATUS
          ? res.data.activities
            ? res.data.activities.map((activity) => ({
                ...activity,
                hotelName: activity.name || '',
                address: activity.providerId || '',
              }))
            : res.data.hotelDetails || []
          : [];

      dispatch(activityFiltersActions.setAutocompleteItems(items));
      dispatch(activityFiltersActions.setAutocompleteLoading(false));
    } catch (error: any) {
      dispatch(activityFiltersActions.setAutocompleteLoading(false));
      console.error(error);

      // The search session expired server-side; a reload re-bootstraps it.
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        (error?.response?.data[ZERO_ITEM] === SESSION_EXPIRED_MESSAGE ||
          error?.response?.data[ZERO_ITEM]?.errorMessage === SESSION_SEARCH_EXPIRED_MESSAGE)
      ) {
        window.location.reload();
      }
    }
  };
};
